import React, { useState } from 'react';
    import { motion } from 'framer-motion';
    import SafeIcon from '../../common/SafeIcon';
    import { FiFilePlus, FiFileText, FiCheckSquare, FiSend, FiUsers, FiMessageSquare } from 'react-icons/fi';
    import { useAuth } from '../../context/AuthContext';

    const stats = [
      { name: 'New Referrals', value: '12', icon: FiFilePlus, color: 'bg-blue-100', textColor: 'text-blue-600' },
      { name: 'Pending Review', value: '5', icon: FiFileText, color: 'bg-yellow-100', textColor: 'text-yellow-600' },
      { name: 'In Progress', value: '8', icon: FiSend, color: 'bg-indigo-100', textColor: 'text-indigo-600' },
      { name: 'Completed', value: '42', icon: FiCheckSquare, color: 'bg-green-100', textColor: 'text-green-600' },
    ];

    const recentReferrals = [
      { patient: 'Amelia Johnson', from: 'Dr. Chen', status: 'Pending', urgency: 'High' },
      { patient: 'Benjamin Moore', from: 'Dr. Patel', status: 'In Progress', urgency: 'Medium' },
      { patient: 'Olivia Garcia', from: 'Dr. Singh', status: 'Completed', urgency: 'Low' },
      { patient: 'Lucas Williams', from: 'Dr. Rodriguez', status: 'Pending', urgency: 'Medium' },
    ];

    const ProviderDashboard = () => {
      const { user } = useAuth();
      return (
        <div className="bg-brand-light min-h-screen p-4 sm:p-6 lg:p-8">
          <div className="max-w-screen-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-3xl font-bold text-brand-primary">Provider Dashboard</h1>
              <p className="text-gray-500 mt-1">Welcome, {user?.title} {user?.lastName}. Here's your referral summary.</p>
            </motion.div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
              {stats.map((stat) => (
                <motion.div 
                  key={stat.name}
                  className="bg-white p-6 rounded-xl shadow-md border border-gray-100"
                  whileHover={{ y: -5, boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' }}
                >
                  <div className="flex items-center space-x-4">
                    <div className={`p-3 rounded-full ${stat.color}`}>
                      <SafeIcon icon={stat.icon} className={`h-6 w-6 ${stat.textColor}`} />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-brand-primary">{stat.value}</p>
                      <p className="text-sm font-medium text-gray-500">{stat.name}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Recent Referrals */}
              <div className="lg:col-span-2 bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <h2 className="text-xl font-bold text-brand-primary mb-4">Recent Referrals</h2>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="py-2 text-sm font-semibold text-gray-500">Patient</th>
                        <th className="py-2 text-sm font-semibold text-gray-500">From</th>
                        <th className="py-2 text-sm font-semibold text-gray-500">Urgency</th>
                        <th className="py-2 text-sm font-semibold text-gray-500">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentReferrals.map((ref, i) => (
                        <motion.tr 
                          key={i} 
                          className="border-b border-gray-100"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          transition={{ delay: 0.2 + i * 0.1 }}
                        >
                          <td className="py-3 font-medium text-gray-800">{ref.patient}</td>
                          <td className="py-3 text-gray-600">{ref.from}</td>
                          <td className="py-3">
                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                              ref.urgency === 'High' ? 'bg-red-100 text-red-700' :
                              ref.urgency === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-green-100 text-green-700'
                            }`}>{ref.urgency}</span>
                          </td>
                          <td className="py-3 text-gray-600">{ref.status}</td>
                        </motion.tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Quick Actions */}
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <h2 className="text-xl font-bold text-brand-primary mb-4">Quick Actions</h2>
                <div className="space-y-3">
                  <motion.button whileHover={{ scale: 1.02 }} className="w-full flex items-center space-x-3 p-3 bg-brand-primary text-white rounded-lg font-semibold hover:bg-opacity-90 transition-colors">
                    <SafeIcon icon={FiFilePlus} /><span>Create New Referral</span>
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.02 }} className="w-full flex items-center space-x-3 p-3 bg-brand-light text-brand-primary rounded-lg font-semibold border border-gray-200 hover:bg-gray-200 transition-colors">
                    <SafeIcon icon={FiMessageSquare} /><span>Message a Colleague</span>
                  </motion.button>
                  <motion.button whileHover={{ scale: 1.02 }} className="w-full flex items-center space-x-3 p-3 bg-brand-light text-brand-primary rounded-lg font-semibold border border-gray-200 hover:bg-gray-200 transition-colors">
                    <SafeIcon icon={FiUsers} /><span>Visit Community Portal</span>
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    };

    export default ProviderDashboard;